import { useMemo } from 'react'
import type { SelectOption } from '../../../../components/SelectCustom'
import type { ShopLanguage, ShopLocale, ShopMessageKey } from '../../../../i18n'
import type { CustomerOrder } from '../../../customerOrdersApi'
import { buildActiveOrderFilterChips } from '../ordersFilterChips'
import { computeFilteredOrders } from '../ordersFilterEngine'
import { buildOrderDateFilterOptions, buildOrderSortOptions } from '../ordersFilterOptions'
import type { ActiveFilterChip, FilteredOrderRow } from '../ordersListSectionTypes'
import {
  buildVisiblePages,
  ORDER_PAGE_SIZE_OPTIONS,
  type OrderPageSizeOption,
  type OrderVisualStatus,
} from '../ordersListShared'
import type { OrdersListFlowState } from './OrdersListFlow.types'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type UseOrdersListFlowViewModelInput = {
  state: OrdersListFlowState
  orders: CustomerOrder[]
  language: ShopLanguage
  locale: ShopLocale
  t: TranslateFn
  onResetDateFilter: () => void
  onRemoveStatusFilter: (status: OrderVisualStatus) => void
  onResetPriceRange: () => void
}

type UseOrdersListFlowViewModelOutput = {
  dateFormatter: Intl.DateTimeFormat
  sortOptions: SelectOption[]
  dateFilterOptions: SelectOption[]
  pageSizeOptions: SelectOption[]
  filteredOrders: FilteredOrderRow[]
  totalPages: number
  visiblePages: number[]
  pagination: { from: number; to: number }
  paginatedOrders: FilteredOrderRow[]
  activeFilterChips: ActiveFilterChip[]
}

export const useOrdersListFlowViewModel = ({
  state,
  orders,
  language,
  locale,
  t,
  onResetDateFilter,
  onRemoveStatusFilter,
  onResetPriceRange,
}: UseOrdersListFlowViewModelInput): UseOrdersListFlowViewModelOutput => {
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
      }),
    [locale],
  )

  const sortOptions = useMemo<SelectOption[]>(() => buildOrderSortOptions(t), [t])

  const dateFilterOptions = useMemo<SelectOption[]>(() => buildOrderDateFilterOptions(t), [t])

  const pageSizeOptions = useMemo<SelectOption[]>(
    () =>
      ORDER_PAGE_SIZE_OPTIONS.map((size) => ({
        value: String(size),
        label: language === 'it' ? `${size} righe` : `${size} rows`,
      })),
    [language],
  )

  const filteredOrders = useMemo<FilteredOrderRow[]>(
    () =>
      computeFilteredOrders({
        orders,
        searchValue: state.searchValue,
        priceMin: state.priceMin,
        priceMax: state.priceMax,
        dateFilter: state.dateFilter,
        customDateFrom: state.customDateFrom,
        customDateTo: state.customDateTo,
        sortBy: state.sortBy,
        statusFilters: state.statusFilters,
      }),
    [orders, state],
  )

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / state.pageSize))

  const visiblePages = useMemo(
    () => buildVisiblePages(state.currentPage, totalPages),
    [state.currentPage, totalPages],
  )

  const pagination = useMemo(() => {
    if (filteredOrders.length === 0) return { from: 0, to: 0 }
    const from = (state.currentPage - 1) * state.pageSize + 1
    const to = Math.min(state.currentPage * state.pageSize, filteredOrders.length)
    return { from, to }
  }, [filteredOrders.length, state.currentPage, state.pageSize])

  const paginatedOrders = useMemo(() => {
    const startIndex = (state.currentPage - 1) * state.pageSize
    return filteredOrders.slice(startIndex, startIndex + state.pageSize)
  }, [filteredOrders, state.currentPage, state.pageSize])

  const activeFilterChips = useMemo<ActiveFilterChip[]>(
    () =>
      buildActiveOrderFilterChips({
        dateFilter: state.dateFilter,
        customDateFrom: state.customDateFrom,
        customDateTo: state.customDateTo,
        dateFormatter,
        statusFilters: state.statusFilters,
        language,
        priceMin: state.priceMin,
        priceMax: state.priceMax,
        t,
        onResetDateFilter,
        onRemoveStatusFilter,
        onResetPriceRange,
      }),
    [
      dateFormatter,
      language,
      onRemoveStatusFilter,
      onResetDateFilter,
      onResetPriceRange,
      state.customDateFrom,
      state.customDateTo,
      state.dateFilter,
      state.priceMax,
      state.priceMin,
      state.statusFilters,
      t,
    ],
  )

  return {
    dateFormatter,
    sortOptions,
    dateFilterOptions,
    pageSizeOptions,
    filteredOrders,
    totalPages,
    visiblePages,
    pagination,
    paginatedOrders,
    activeFilterChips,
  }
}
